import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Flame, Layers, Shield } from 'lucide-react';
import WhatsAppCTA from '../components/WhatsAppCTA';
import Testimonials from '../components/Testimonials';
import OrnateDivider from '../components/OrnateDivider';
import LatestFromThePath from '../components/LatestFromThePath';
import EnrollBlock from '../components/EnrollBlock';
import EditableText from '../components/EditableText';
import SEO from '../components/SEO';
import { authUrlWithNext } from '../lib/whatsapp';
import { useEnquiry } from '../context/EnquiryContext';

const Home = () => {
  const navigate = useNavigate();
  const { openEnquiry } = useEnquiry();
  const { user } = useAuth();

  // Helper function to render symbolic icons
  const renderLevelIcon = (iconType) => {
    const iconProps = { className: "text-primary", size: 80 };
    
    switch(iconType) {
      case 'lotus':
        return (
          <svg width="80" height="80" viewBox="0 0 100 100" className="text-primary">
            <path d="M50 20 C30 30, 20 50, 50 70 C80 50, 70 30, 50 20Z" fill="currentColor" opacity="0.3"/>
            <path d="M50 30 C35 35, 30 50, 50 65 C70 50, 65 35, 50 30Z" fill="currentColor" opacity="0.5"/>
            <circle cx="50" cy="50" r="8" fill="currentColor"/>
            <path d="M42 50 C42 45, 45 42, 50 42 C55 42, 58 45, 58 50" stroke="currentColor" fill="none" strokeWidth="2"/>
          </svg>
        );
      case 'crescent':
        return (
          <svg width="80" height="80" viewBox="0 0 100 100" className="text-primary">
            <path d="M35 25 A25 25 0 1 1 35 75 A20 20 0 1 0 35 25" fill="currentColor" opacity="0.7"/>
            <circle cx="30" cy="30" r="3" fill="currentColor"/>
          </svg>
        );
      case 'sun':
        return (
          <svg width="80" height="80" viewBox="0 0 100 100" className="text-primary">
            <circle cx="50" cy="50" r="18" fill="currentColor" opacity="0.8"/>
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <line
                key={i}
                x1="50"
                y1="50"
                x2={50 + 32 * Math.cos((angle * Math.PI) / 180)}
                y2={50 + 32 * Math.sin((angle * Math.PI) / 180)}
                stroke="currentColor"
                strokeWidth="3"
                opacity="0.6"
              />
            ))}
          </svg>
        );
      case 'eye':
        return (
          <svg width="80" height="80" viewBox="0 0 100 100" className="text-primary">
            <ellipse cx="50" cy="50" rx="35" ry="20" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.6"/>
            <circle cx="50" cy="50" r="12" fill="currentColor" opacity="0.7"/>
            <circle cx="50" cy="50" r="6" fill="currentColor"/>
            <ellipse cx="50" cy="50" rx="35" ry="20" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.6" transform="rotate(180 50 50)"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const levels = [
    {
      number: 1,
      title: "Grahita Shakti",
      subtitle: "Building the capacity to receive",
      description: "The system is prepared — body, breath, and attention aligned — so that awareness can be received without distortion or loss.",
      locked: false,
      icon: "lotus"
    },
    {
      number: 2,
      title: "Nimeṣa",
      subtitle: "Turning inward",
      description: "Attention withdraws from its outward fixation and begins to rest within. Not as suppression, but as a natural return.",
      locked: !user || user.current_level < 2,
      icon: "crescent"
    },
    {
      number: 3,
      title: "Unmeṣa",
      subtitle: "Opening into expanded awareness",
      description: "What was seen as 'outside' is no longer separate. Awareness extends without losing its centre.",
      locked: !user || user.current_level < 3,
      icon: "sun"
    },
    {
      number: 4,
      title: "Pratyabhijñā",
      subtitle: "Recognition",
      description: "Nothing new is attained. What has always been is simply recognised and stabilised.",
      locked: !user || user.current_level < 4,
      icon: "eye"
    }
  ];

  return (
    <div className="min-h-screen mystical-bg">
      <SEO
        title="MyShaktiverse — The Game of Consciousness"
        description="Stillness is the ground. Movement is the pulse. The body is where both meet. Begin with the Grahita Śakti foundation programme."
      />
      {/* Hero Section - Upper half of the image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_inner-ascent-1/artifacts/ztzz1tp9_ChatGPT%20Image%20Apr%208%2C%202026%2C%2003_04_05%20PM.png)',
            backgroundPosition: 'center 20%'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/90" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="hero-breathe" data-testid="hero-breathing-title">
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-heading font-bold mb-4 golden-text text-glow uppercase tracking-widest">
                <EditableText id="home.hero.title1" defaultValue="ENTER THE" />
              </h1>
              <h1 className="text-5xl sm:text-7xl lg:text-8xl font-heading font-black mb-8 golden-text text-glow uppercase tracking-widest">
                <EditableText id="home.hero.title2" defaultValue="SHAKTIVERSE" />
              </h1>
              <p className="text-xl sm:text-2xl lg:text-3xl font-heading text-primary mb-4 tracking-wider uppercase">
                <EditableText id="home.hero.tagline" defaultValue="THE GAME OF CONSCIOUSNESS" />
              </p>
            </div>
            <p className="text-base sm:text-lg lg:text-xl text-primary/80 max-w-2xl mx-auto mb-12 leading-relaxed">
              <EditableText
                id="home.hero.subheadline"
                defaultValue="Ancient Siddha transmissions. Deeper states of awareness. A guided path into the depths of consciousness using Siddha Kriyas."
                multiline
              />
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button
                type="button"
                onClick={() => openEnquiry('live')}
                className="ornate-button px-10 py-4 text-lg font-heading text-primary uppercase tracking-wider rounded-lg"
                data-testid="hero-join-live-batch"
              >
                <EditableText id="home.hero.cta.primary" defaultValue="Join a Live Batch" />
              </button>
              <button
                onClick={() => navigate('/courses/1')}
                className="ornate-button px-10 py-4 text-lg font-heading text-primary uppercase tracking-wider rounded-lg"
                data-testid="hero-explore-courses"
              >
                <EditableText id="home.hero.cta.secondary" defaultValue="Explore Self-Paced Courses" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Progression Panel - With Sri Yantra Background */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_inner-ascent-1/artifacts/6xh0x3zy_ChatGPT%20Image%20Apr%208%2C%202026%2C%2004_28_32%20PM.png)',
            backgroundPosition: 'center',
            opacity: '0.15'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-black/80" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <OrnateDivider label="The Path" variant="lotus" className="mb-8 max-w-2xl mx-auto" />
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary/70 mb-4">
              <EditableText id="home.siddha.label" defaultValue="THE SIDDHA METHOD" />
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold golden-text text-glow tracking-wide mb-6">
              <EditableText
                id="home.siddha.heading"
                defaultValue="Dhyana mastery with Siddha Kriyas"
              />
            </h2>
            <p className="text-base sm:text-lg text-primary/80 leading-relaxed max-w-4xl mx-auto">
              <EditableText
                id="home.siddha.body"
                defaultValue="Most meditation practices work with the surface of the mind. The Beginner friendly Siddha Kriyas go deeper. Developed and preserved within the Himalayan Siddha lineage, these powerful inner practices were designed to sequentially awaken higher states of awareness. Through a structured progression of Prāṇāyāma (breath regulation), Bandha (energetic locks), Mudrā (energetic seals), Dhāraṇā (concentration), and Dhyāna (meditative absorption), our guides help you apply these kriyas step by step, building the capacity to receive higher states of consciousness with stability and depth. Whether you are new to meditation or have practised for years, this path meets you where you are and guides you further."
                multiline
              />
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                label: 'home.siddha.card4.label', text: 'home.siddha.card4.text',
                defaultLabel: 'Siddha Healing',
                defaultText: 'Ancient Siddha methods for inner work, healing and trauma recovery, protection from psychic attacks, and mental wellness.',
                ctaId: 'home.siddha.card4.cta',
                ctaDefault: 'Book a Session',
                onClick: () => openEnquiry('healing'),
                ctaTestid: 'siddha-card-healing-cta',
              },
              {
                icon: Flame,
                label: 'home.siddha.card1.label', text: 'home.siddha.card1.text',
                defaultLabel: 'Live Classes',
                defaultText: 'Live online classes and energy exchange with Sanjay Ji, advancing you in the journey of self discovery through mastering Siddha kriyas (beginner friendly)',
                ctaId: 'home.siddha.card1.cta',
                ctaDefault: 'Enquire About Batches',
                onClick: () => openEnquiry('live'),
                ctaTestid: 'siddha-card-live-cta',
              },
              {
                icon: Layers,
                label: 'home.siddha.card2.label', text: 'home.siddha.card2.text',
                defaultLabel: 'Grahita Śakti',
                defaultText: 'A self-guided programme. Practice at your own rhythm. Includes one personal session with Sanjay Ji to fine-tune your practice.',
                ctaId: 'home.siddha.card2.cta',
                ctaDefault: 'Begin the Programme',
                onClick: () => navigate('/courses/1'),
                ctaTestid: 'siddha-card-grahita-cta',
              },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="card-mystical rounded-2xl p-6 text-center flex flex-col items-center"
                  data-testid={`siddha-card-${i + 1}`}
                >
                  <div className="mb-5 mandala-icon">
                    <Icon size={40} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-heading font-bold text-primary mb-3 uppercase tracking-wider">
                    <EditableText id={c.label} defaultValue={c.defaultLabel} />
                  </h3>
                  <p className="text-sm text-primary/80 leading-relaxed flex-1 mb-6">
                    <EditableText id={c.text} defaultValue={c.defaultText} multiline />
                  </p>
                  <button
                    type="button"
                    onClick={c.onClick}
                    className="ornate-button px-6 py-2.5 text-sm font-heading text-primary uppercase tracking-wider rounded-lg mt-auto"
                    data-testid={c.ctaTestid}
                  >
                    <EditableText id={c.ctaId} defaultValue={c.ctaDefault} />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest From the Path — blog homepage row */}
      <LatestFromThePath />

      {/* Two-option enroll card — between Latest From the Path and Remembrance */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="home-enroll-section">
        <div className="max-w-4xl mx-auto">
          <EnrollBlock onSelfPacedClick={() => navigate('/courses/1')} />
        </div>
      </section>

      {/* Teacher — Sanjay Ji (compact) */}
      <section className="pb-4 px-4 sm:px-6 lg:px-8" data-testid="home-teacher-section">
        <div className="max-w-3xl mx-auto card-mystical rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
          <div className="shrink-0">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-2 border-primary/40 bg-primary/10">
              <img
                src="https://customer-assets.emergentagent.com/job_81366ea3-8fba-49ac-8967-39c362fb8b9e/artifacts/ico6mczy_New%20Hero%20Image.png"
                alt="Sanjay Ji"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="home-teacher-photo"
              />
            </div>
          </div>
          <div className="flex-1">
            <div className="text-[11px] font-heading tracking-[0.35em] text-primary/60 uppercase mb-1">
              <EditableText id="home.teacher.eyebrow" defaultValue="Your Teacher" />
            </div>
            <h3 className="text-2xl font-heading golden-text text-glow tracking-wide mb-2">
              <EditableText id="home.teacher.name" defaultValue="Sanjay Ji" />
            </h3>
            <p className="text-sm sm:text-[15px] text-primary/80 italic leading-relaxed">
              <EditableText
                id="home.teacher.oneliner"
                defaultValue={'Only when true silence guides worldly life can one fully embody Mother Shakti\u2019s divine expression.'}
                multiline
              />
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section — HIDDEN (kept in code for easy re-enable; remove the `false &&` to show again) */}
      {false && <Testimonials />}

      {/* WhatsApp CTA */}
      <WhatsAppCTA className="border-t border-primary/20" />
    </div>
  );
};

export default Home;